import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Eye, EyeOff, Mail, Phone, User } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import StyledLink from '../../components/header-footer/StyledLink';
import logger from '../../utils/logger';
import MFALoginModal from '../../components/MFALoginModal';
import MFAMethodSelector, { MFAMethod } from '../../components/MFAMethodSelector';
import PhoneOTPModal from '../../components/PhoneOTPModal';
import EmailOTPModal from '../../components/EmailOTPModal';
import { toast } from 'react-hot-toast';

type LoginMethod = 'email' | 'phone' | 'username';

/**
 * Renders the login page and handles user authentication via Supabase.
 *
 * Redirects authenticated users to the home page. Allows users to sign in with email and password, toggle password visibility, and opt to remember their session. If "Remember me" is selected, persists the session token in localStorage and stores the email in a cookie for 30 days. Performs a Supabase health check on mount and clears authentication errors on unmount.
 *
 * @remark
 * If "Remember me" is checked, the session token is saved to localStorage and the user's email is stored in a cookie named `mozo_remember_email` with a 30-day expiry.
 */
export default function Login() {
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showMFAMethodSelector, setShowMFAMethodSelector] = useState(false);
  const [showPhoneOTPModal, setShowPhoneOTPModal] = useState(false);
  const [showEmailOTPModal, setShowEmailOTPModal] = useState(false);
  const [selectedMFAMethod, setSelectedMFAMethod] = useState<MFAMethod | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearError, signIn } = useAuth();
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/feed');
    }
  }, [user, navigate]);
  
  // Check for verification success message in location state
  useEffect(() => {
    const state = location.state as { verified?: boolean; email?: string; message?: string } | null;
    
    if (state?.verified && state?.email) {
      setEmail(state.email);
      setSuccessMessage(state.message || 'Email verified successfully! You can now log in.');
      
      // Clear the state after reading it
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Autofocus email input on mount
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // Simplified Supabase connection check
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const { error } = await supabase.from('users').select('count').limit(1);
        if (error) {
          logger.warn('Supabase connection issue:', error.message);
        }
      } catch (err) {
        logger.warn('Connection check failed:', err);
      }
    };

    checkConnection();
  }, []);

  // Clear auth errors on unmount
  useEffect(() => {
    return () => {
      clearError();
    };
  }, []); // Empty dependency array - only run on unmount

  // Handle MFA verification
  const handleMFAVerify = async (code: string): Promise<boolean> => {
    try {
      // Handle different MFA methods
      if (selectedMFAMethod === 'authenticator') {
        // Get TOTP factors and create challenge
        const { data: factors } = await supabase.auth.mfa.listFactors();
        if (!factors || !factors.totp || factors.totp.length === 0) {
          toast.error('No authenticator app found. Please set up MFA first.');
          return false;
        }
        
        const totpFactor = factors.totp.find((f: any) => f.status === 'verified');
        if (!totpFactor) {
          toast.error('No verified authenticator found.');
          return false;
        }

        const { data: challenge } = await supabase.auth.mfa.challenge({
          factorId: totpFactor.id
        });

        if (!challenge) return false;

        const { error } = await supabase.auth.mfa.verify({
          factorId: totpFactor.id,
          challengeId: challenge.id,
          code
        });

        if (error) {
          toast.error('Invalid verification code. Please try again.');
          return false;
        }
      } else if (selectedMFAMethod === 'phone') {
        // Simulate phone OTP verification
        if (code !== '123456') {
          toast.error('Invalid SMS code. Please try again.');
          return false;
        }
      } else if (selectedMFAMethod === 'email') {
        // Simulate email OTP verification
        if (code !== '654321') {
          toast.error('Invalid email code. Please try again.');
          return false;
        }
      }

      // MFA verification successful - close all modals
      setShowMFAModal(false);
      setShowPhoneOTPModal(false);
      setShowEmailOTPModal(false);
      
      // Handle remember me after successful MFA
      if (rememberMe) {
        const session = (await supabase.auth.getSession()).data.session;
        if (session) {
          localStorage.setItem('supabase.auth.token', JSON.stringify(session));
        }
        
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 30);
        document.cookie = `mozo_remember_email=${email};expires=${expiryDate.toUTCString()};path=/;SameSite=Strict`;
      }

      navigate('/feed');
      return true;
    } catch (err) {
      console.error('MFA verification error:', err);
      toast.error('Verification failed. Please try again.');
      return false;
    }
  };

  // Handle MFA method selection during login
  const handleMFAMethodSelect = (method: MFAMethod) => {
    setSelectedMFAMethod(method);
    setShowMFAMethodSelector(false);
    
    if (method === 'authenticator') {
      setShowMFAModal(true);
    } else if (method === 'phone') {
      setShowPhoneOTPModal(true);
    } else if (method === 'email') {
      setShowEmailOTPModal(true);
    }
  };

  useEffect(() => {
    // Clear any existing errors when component mounts
    clearError();
    
    // Focus email input on mount
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [clearError]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const contactValue = loginMethod === 'email' ? email : loginMethod === 'phone' ? phone : username;
      
      if (!contactValue.trim() || !password.trim()) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Use the universal signIn method from auth context
      await signIn(contactValue, password);

      // If remember me is checked, store the contact info
      if (rememberMe) {
        // Store the contact value in a cookie for 30 days
        const expires = new Date();
        expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
        document.cookie = `mozo_remember_contact=${contactValue}; expires=${expires.toUTCString()}; path=/`;
      }

      // Navigate to the intended page or dashboard
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Check if MFA is required
      if (error.message && (error.message.includes('MFA') || error.message.includes('factor'))) {
        // User has MFA enabled, detect their preferred method and show appropriate modal
        try {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          if (factors && factors.totp && factors.totp.length > 0) {
            // User has TOTP setup, show authenticator modal
            const totpFactor = factors.totp.find((f: any) => f.status === 'verified');
            if (totpFactor) {
              setSelectedMFAMethod('authenticator');
              setShowMFAModal(true);
              setLoading(false);
              return;
            }
          }
          
          // For demo: Check user's stored MFA preference or default to phone
          // In production, this would be stored in user metadata
          const contactValue = loginMethod === 'email' ? email : loginMethod === 'phone' ? phone : username;
          const userMFAMethod = localStorage.getItem(`mfa_method_${contactValue}`) as MFAMethod || 'phone';
          setSelectedMFAMethod(userMFAMethod);
          
          if (userMFAMethod === 'phone') {
            setShowPhoneOTPModal(true);
            toast.success('SMS code sent to your phone');
          } else if (userMFAMethod === 'email') {
            setShowEmailOTPModal(true);
            toast.success('Verification code sent to your email');
          } else {
            // Fallback to method selector if method not recognized
            setShowMFAMethodSelector(true);
          }
          setLoading(false);
          return;
        } catch (mfaError) {
          console.error('MFA detection failed:', mfaError);
          // Fallback to method selector
          setShowMFAMethodSelector(true);
          setLoading(false);
          return;
        }
      }
      
      setError(error.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link
          to="/"
          className="absolute top-4 left-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-white">
          Welcome back
        </h1>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Don't have an account?{' '}
          <StyledLink to="/signup">
            Sign up
          </StyledLink>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-black py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {successMessage && (
            <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {successMessage}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email/Phone/Username Tabs and Input */}
            <div>
              {/* Tab Headers */}
              <div className="flex space-x-1 mb-3">
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    loginMethod === 'email'
                      ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('phone')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    loginMethod === 'phone'
                      ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Phone className="w-4 h-4" />
                  <span>Phone</span>
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('username')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    loginMethod === 'username'
                      ? 'bg-gray-900 dark:bg-gray-700 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span>Username</span>
                </button>
              </div>

              {/* Input Field */}
              <label htmlFor="contact" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {loginMethod === 'email' ? 'Email address' : loginMethod === 'phone' ? 'Phone number' : 'Username'}
              </label>
              <div className="mt-1">
                <div className="relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                  {loginMethod === 'email' ? (
                    <input
                      ref={emailInputRef}
                      id="contact"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                      placeholder="Enter your email"
                    />
                  ) : loginMethod === 'phone' ? (
                    <input
                      id="contact"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="appearance-none block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <input
                      id="contact"
                      name="username"
                      type="text"
                      autoComplete="username"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="appearance-none block w-full px-5 py-4 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                      placeholder="Enter your username"
                    />
                  )}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1">
                <div className="relative bg-white dark:bg-black rounded-2xl shadow-lg overflow-hidden border-0 transition-all duration-300 hover:shadow-xl">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-5 py-4 pr-12 bg-transparent placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm dark:text-white"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <StyledLink to="/forgot-password">
                  Forgot your password?
                </StyledLink>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-4 px-5 border border-transparent rounded-2xl shadow-lg text-sm font-medium text-white bg-[#065900] dark:bg-[#065900] hover:bg-[#054800] dark:hover:bg-[#054800] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#065900] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {showMFAModal && (
        <MFALoginModal
          isOpen={showMFAModal}
          onClose={() => setShowMFAModal(false)}
          onVerify={handleMFAVerify}
          loading={loading}
          userEmail={email}
        />
      )}
      {showMFAMethodSelector && (
        <MFAMethodSelector
          isOpen={showMFAMethodSelector}
          onClose={() => setShowMFAMethodSelector(false)}
          onMethodSelect={handleMFAMethodSelect}
        />
      )}
      {showPhoneOTPModal && (
        <PhoneOTPModal
          isOpen={showPhoneOTPModal}
          onClose={() => setShowPhoneOTPModal(false)}
          onVerify={(code) => handleMFAVerify(code)}
          loading={loading}
        />
      )}
      {showEmailOTPModal && (
        <EmailOTPModal
          isOpen={showEmailOTPModal}
          onClose={() => setShowEmailOTPModal(false)}
          onVerify={(code) => handleMFAVerify(code)}
          loading={loading}
        />
      )}
    </div>
  );
}